<template>
    <transition>
        <div class="container-fluid page-content">
            <!-- Modern Page Header -->
            <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
                <div>
                    <h4 class="page-title text-slate-800 fw-bold mb-1">Mobile Wallet</h4>
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb mb-0">
                            <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                            <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">Settings</a></li>
                            <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Mobile Wallet List</li>
                        </ol>
                    </nav>
                </div>
                <div>
                    <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal" v-if="permission['mobile-create']">
                        <i class="fas fa-plus"></i> Add New Wallet
                    </button>
                </div>
            </div>

            <!-- Modern Filter Dashboard Bar -->
            <div class="card border-0 shadow-sm filter-card mb-4" v-if="items.length > 0 || tableData.search">
                <div class="card-body">
                    <div class="row align-items-center justify-content-between gap-3 gap-md-0">
                        <div class="col-12 col-md-auto d-flex align-items-center gap-2">
                            <span class="text-muted text-nowrap">Show</span>
                            <select class="form-select form-select-custom length-select" v-model="tableData.length" @change="fetchData()">
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option :value="pagination.total">All</option>
                            </select>
                            <span class="text-muted text-nowrap">Entries</span>
                        </div>
                        <div class="col-12 col-md-4">
                            <div class="search-input-wrapper">
                                <input type="text" class="form-control form-control-custom search-input" v-model="tableData.search" placeholder="Search..." @input="fetchData()">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modern Table List Card -->
            <div class="card border-0 shadow-sm table-card">
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table table-premium mb-0" v-if="!loading && items.length > 0">
                            <thead>
                                <tr>
                                    <th style="width: 80px;">SL</th>
                                    <th>Mobile Wallet Type</th>
                                    <th>Agent Name</th>
                                    <th>Mobile Number</th>
                                    <th>Charge %</th>
                                    <th style="width: 150px;">Status</th>
                                    <th class="text-end" style="width: 120px;">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, i) in items" :key="i">
                                    <td>{{ pagination.from + i }}</td>
                                    <td class="fw-bold text-slate-800">{{ item.mobile_wallet }}</td>
                                    <td>{{ item.agent_name }}</td>
                                    <td><code>{{ item.mobile_number }}</code></td>
                                    <td>{{ item.charge_percent }}%</td>
                                    <td>
                                        <span v-if="item.status == 1" class="badge-role badge-role-active">Active</span>
                                        <span v-if="item.status == 0" class="badge-role badge-role-inactive">Inactive</span>
                                    </td>
                                    <td class="text-end">
                                        <div class="dropdown d-inline-block">
                                            <button class="btn btn-action-dropdown dropdown-toggle arrow-none" data-bs-toggle="dropdown" aria-expanded="false">
                                                <i class="fas fa-ellipsis-v"></i>
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-end shadow-sm border-0">
                                                <a href="javascript:void(0);" class="dropdown-item text-warning" @click="edit(item)" v-if="permission['mobile-wallet-edit']">
                                                    <i class="fas fa-edit me-2"></i>Edit
                                                </a>
                                                <a href="javascript:void(0);" class="dropdown-item text-danger" @click="deleteItem(item)" v-if="permission['mobile-wallet-delete']">
                                                    <i class="fas fa-trash-alt me-2"></i>Delete
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Empty State -->
                        <div class="text-center py-5" v-if="!loading && items.length === 0">
                            <div class="empty-state-icon mb-3">
                                <i class="fas fa-wallet text-muted fa-3x"></i>
                            </div>
                            <h5 class="text-slate-700 fw-bold">No Mobile Wallets Found</h5>
                            <p class="text-muted mb-0">Try adjusting your search or add a new mobile wallet.</p>
                        </div>

                        <!-- Loading State -->
                        <div class="text-center py-5" v-if="loading">
                            <div class="spinner-premium mb-3 mx-auto"></div>
                            <h6 class="text-indigo fw-semibold">Loading data...</h6>
                        </div>
                    </div>
                </div>

                <!-- Table Footer with Pagination -->
                <div class="card-footer bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3" v-if="!loading && items.length > 0">
                    <div class="text-muted small">
                        Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} entries
                    </div>
                    <Pagination :pagination="pagination" :language="lang" @onChangePage="setPage"></Pagination>
                </div>
            </div>

            <!-- Modal -->
            <Modal @close="toggleModal()" :modalActive="modalActive">
                <div class="modal-content border-0 shadow-lg modal-premium modal-premium-sm">
                    <div class="modal-header border-0 pb-0">
                        <h4 class="fw-bold m-0 text-slate-800">{{ editMode ? 'Update Mobile Wallet' : 'Create New Mobile Wallet' }}</h4>
                        <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                    </div>
                    <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                        <div class="modal-body py-4">
                            <div class="mb-3">
                                <label for="company_id" class="form-label-custom text-slate-700 fw-semibold mb-2">Company *</label>
                                <select class="form-select form-select-custom" :class="{'is-invalid': errors.company_id}" v-model="form.company_id" @change="onkeyPress('company_id')" id="company_id">
                                    <option value="0">Select company</option>
                                    <option v-for="(company, index) in companies" :value="company.id" :key="index">
                                        {{ company.name }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" v-if="errors.company_id">
                                    {{ errors.company_id[0] }}
                                </div>
                            </div>
                            <div class="mb-3">
                                <label for="mobile_wallet" class="form-label-custom text-slate-700 fw-semibold mb-2">Mobile Wallet Type *</label>
                                <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.mobile_wallet}" @keypress="onkeyPress('mobile_wallet')" v-model="form.mobile_wallet" id="mobile_wallet" placeholder="Enter mobile wallet type" autocomplete="off">
                                <div class="invalid-feedback" v-if="errors.mobile_wallet">
                                    {{ errors.mobile_wallet[0] }}
                                </div>
                            </div>
                            <div class="mb-3">
                                <label for="agent_name" class="form-label-custom text-slate-700 fw-semibold mb-2">Agent Name *</label>
                                <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.agent_name}" @keypress="onkeyPress('agent_name')" v-model="form.agent_name" id="agent_name" placeholder="Enter agent name" autocomplete="off">
                                <div class="invalid-feedback" v-if="errors.agent_name">
                                    {{ errors.agent_name[0] }}
                                </div>
                            </div>
                            <div class="mb-3">
                                <label for="mobile_number" class="form-label-custom text-slate-700 fw-semibold mb-2">Mobile Number *</label>
                                <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.mobile_number}" @keypress="onkeyPress('mobile_number')" v-model="form.mobile_number" id="mobile_number" placeholder="Enter mobile number" autocomplete="off">
                                <div class="invalid-feedback" v-if="errors.mobile_number">
                                    {{ errors.mobile_number[0] }}
                                </div>
                            </div>
                            <div class="mb-3">
                                <label for="charge_percent" class="form-label-custom text-slate-700 fw-semibold mb-2">Charge Percent (%) *</label>
                                <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.charge_percent}" @keypress="onkeyPress('charge_percent')" v-model="form.charge_percent" id="charge_percent" placeholder="Enter charge percentage" autocomplete="off">
                                <div class="invalid-feedback" v-if="errors.charge_percent">
                                    {{ errors.charge_percent[0] }}
                                </div>
                            </div>
                            <div class="mb-0">
                                <label for="ustatus" class="form-label-custom text-slate-700 fw-semibold mb-2">Status</label>
                                <select class="form-select form-select-custom" :class="{'is-invalid': errors.status}" v-model="form.status" @change="onkeyPress('status')" id="ustatus">
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                                <div class="invalid-feedback" v-if="errors.status">
                                    {{ errors.status[0] }}
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer border-0 pt-0 justify-content-end gap-2">
                            <button type="button" class="btn btn-outline-premium px-4" @click="toggleModal()">Cancel</button>
                            <button type="submit" class="btn btn-premium px-4" :disabled="disabled">
                                <span v-show="isSubmit" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {{ btn }}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    </transition>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { useProductStore } from '@/stores/product';
import { useRequisitionProductStore } from '@/stores/requisitionProduct';


import Modal from "../helper/Modal.vue";
import Datatable from '@/components/Datatable.vue';
import Pagination from '@/components/Pagination.vue';
import Form from "vform";
import axios from "axios";
export default {
    name: "MobileWallets",
    components: {
        Modal,
        Datatable,
        Pagination,
    },
    props:{
        language: {
          type: Object,
          default: () => {
            return {
              lengthMenu: null,
              info: null,
              zeroRecords: null, 
              search: null
            }
          },
        },
    },
    data() {
        return {
            isSubmit: false,
            showModal: false,
            editMode:false,
            disabled: false,
            modalActive:false,
            errors: {},
            btn:'Create',
            items: [],
            companies: [],
            loading: true,
            form: new Form({
                id: '',
                mobile_wallet: '',
                agent_name: '',
                mobile_number:'',
                charge_percent:'',
                company_id:'',
                status: 1,
            }), 
            columns: [ 
                {
                    label: 'Mobile wallet type ',
                    name: 'name',           
                    width: '25%'
                }, 
                {
                    label: 'Agent name',
                    name: 'value',           
                    width: '20%'
                }, 
                {
                    label: 'Mobile number',
                    name: 'value',           
                    width: '20%'
                }, 
                {
                    label: 'Charge %',
                    name: 'value',           
                    width: '15%'
                }, 
                {
                    label: 'Status',
                    name: 'status',
                    isSearch: false, 
                    width: '10%'
                },
                {
                    label: 'Actions',            
                    name: '',
                    isSearch: false, 
                    isAction: true,
                    width: '10%',

                }
            ],  
            tableData: {
                draw: 0,
                length: 10,
                search: '',
                column: 0,
                dir: 'desc',
                sortKey: 'name', 
            }, 
            lang: {
                lengthMenu: this.$props.language.lengthMenu ? this.$props.language.lengthMenu : 'Show_MENU_entries',
                info: this.$props.language.info ? this.$props.language.info : 'Showing_FROM_to_TO_of_TOTAL_entries',
                zeroRecords: this.$props.language.zeroRecords ? this.$props.language.zeroRecords : 'No data available in table.', 
                search: this.$props.language.search ? this.$props.language.search : 'Search'
            },
            pagination: {
                lastPage: '',
                currentPage: '',
                total: '',
                lastPageUrl: '',
                nextPageUrl: '',
                prevPageUrl: '',
                from: '',
                to: '',
                links:[],
            } 
        }
    },
    created() {
        this.fetchData();
        this.fetchCompany();
    },

    methods: {
        toggleModal: function() {
            this.modalActive = !this.modalActive;
            if(!this.modalActive){
                this.editMode = false;
                this.btn='Create';
            } 
            this.errors = '';
            this.isSubmit = false;
            this.form.reset();
        }, 

        fetchCompany() { 
            axios.get(this.apiUrl+'/companies',this.headerjson)
            .then((res) => { 
                this.companies = res.data.data;
            })
            .catch((err) => {
                this.$toast.error(err.response.data.message); 
            });
        },

        edit: function(item) {
            this.btn='Update';
            this.editMode = true;
            this.toggleModal();
            this.form.fill(item); 
        },
        submitForm: function(e) {             
            this.isSubmit = true;
            this.disabled = true; 
            if(this.editMode){ 
                var postEvent = axios.put(this.apiUrl+'/mobile_wallets/'+this.form.id, this.form, this.headerjson );
            }else{  
                var postEvent = axios.post(this.apiUrl+'/mobile_wallets', this.form,  this.headerjson);
            }
            postEvent.then(res => { 
                this.isSubmit = false;
                this.disabled = false;
                if(res.status == 200){
                    this.toggleModal();
                    this.fetchData(this.apiUrl+'/mobile_wallets?page='+this.pagination.currentPage);
                    this.$toast.success(res.data.message); 
                }else{
                    this.$toast.error(res.data.message);
                }
            }).catch(err => {  
                this.isSubmit = false;
                this.disabled = false;
                this.$toast.error(err.response.data.message);
                if(err.response.status == 422){
                    this.errors = err.response.data.errors 
                }
            })
        },

        onkeyPress: function(field) { 
            for (var k in this.errors){     // Loop through the object
                if(k==field){      // If the current key contains the string we're looking for 
                    delete this.errors[k];  // Delete obj[key];
                }
            }  
        },
        deleteItem: function(item) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!", 
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => { 
                if (result.value) { 
                    axios.delete(this.apiUrl+'/mobile_wallets/'+item.id, this.headerjson)
                    .then(res => {
                        if(res.status == 200){ 
                            this.fetchData(this.apiUrl+'/mobile_wallets?page='+this.pagination.currentPage);
                            this.$toast.success(res.data.message); 
                        }else{
                            this.$toast.error(res.data.message);
                        }
                    }).catch(err => {  
                        this.$toast.error(err.response.data.message);
                        if(err.response.status == 422){
                            this.errors = err.response.data.errors 
                        }
                    }) 
                }else{
                    // this.$toast.error(`Hey! I'm here`);
                    // this.$toast.warning(`Hey! I'm here`);
                    // this.$toast.info(`Hey! I'm here`)
                }
            }); 
        },
        fetchData(url = this.apiUrl+'/mobile_wallets') {
            this.tableData.draw++;
            axios.get(url, {params:this.tableData, headers: this.headerparams})
            .then((response) => { 
                this.items = response.data.data;   
                this.configPagination(response.data.meta); 
            })
            .catch(errors => {
                this.$toast.error(errors);
            })
            .finally((fres) => {
                this.loading = false;
            });
        },

        configPagination(data){
            this.pagination.lastPage = data.last_page;
            this.pagination.currentPage = data.current_page;
            this.pagination.total   = data.total ? data.total : 0;
            this.pagination.lastPageUrl = data.last_page_url;
            this.pagination.nextPageUrl = data.next_page_url;
            this.pagination.prevPageUrl = data.prev_page_url;
            this.pagination.from = data.from ? data.from : 0;
            this.pagination.to = data.to ? data.to : 0;  
            this.pagination.links = data.links;
        },

        sortBy(key,sortable) {
            this.tableData.sortKey = key; 
            this.tableData.column = this.getIndex(this.columns, 'name', key);
            this.tableData.dir = sortable;  
            this.fetchData();
        },
        setPage(data){  
            this.fetchData(data.url); 
        },
        getIndex(array, key, value) {
            return array.findIndex(i => i[key] == value)
        }
          
    },
    destroyed() {},
    mounted() {
        window.scrollTo(0, 0);
    },
    computed: {
        permission() {
            let pname = this.$route.meta.parent_module;
            let module_name = this.$route.meta.module_name;
            let path_name = this.$route.path; 
            let data = '';
            if(this.$route.meta.parent_module){
                data = this.$store.getters.userAllPermissions[pname][0].children[path_name]
            }else{
                data = this.$store.getters.userAllPermissions[module_name][0].other_actions; 
            } 
            return data;
        },

    }
}
</script>